import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Facebook, Instagram, Twitter, Youtube, Linkedin, MapPin, Phone, Mail, Heart, ArrowRight, Sparkles } from 'lucide-react';
import EnquiryDialog from './EnquiryDialog';
import { useT } from '../i18n/I18nProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  cta_kicker: 'Make an impact today',
  cta_headline_prefix: 'Help us change another',
  cta_headline_emphasis: '15,000 lives',
  cta_button_label: 'Donate Now',
  address_lines: ['4B, Persiaran Zaaba,', 'Taman Tun Dr Ismail,', '60000 Kuala Lumpur'],
  phone: '03-7498 7140',
  phone_href: 'tel:+60374987140',
  whatsapp_display: '+60 12-675 9467 (WhatsApp)',
  whatsapp_href: 'https://wa.me/60126759467',
  email: 'admin@ewrf.org.my',
  facebook_url: '',
  instagram_url: '',
  twitter_url: '',
  youtube_url: '',
  linkedin_url: '',
  copyright_text: '© 2026 EWRF Malaysia · All rights reserved · Reg. PPM-001-14-02011979 · Tax-exempt under Section 44(6) 🇲🇾',
};

const Footer = () => {
  const t = useT();
  const [c, setC] = useState(DEFAULTS);
  const [openEnquiry, setOpenEnquiry] = useState(null); // 'counselling' | 'volunteer' | null
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/content/footer`);
        if (data && data.id) setC({ ...DEFAULTS, ...data });
      } catch {
        /* silent */
      }
    })();
  }, []);

  const socials = [
    { Icon: Facebook, href: c.facebook_url, label: 'Facebook', color: 'hover:bg-[#1877F2]' },
    { Icon: Instagram, href: c.instagram_url, label: 'Instagram', color: 'hover:bg-gradient-to-br hover:from-[#E1306C] hover:via-[#F77737] hover:to-[#FCAF45]' },
    { Icon: Twitter, href: c.twitter_url, label: 'Twitter', color: 'hover:bg-[#1DA1F2]' },
    { Icon: Youtube, href: c.youtube_url, label: 'YouTube', color: 'hover:bg-[#FF0000]' },
    { Icon: Linkedin, href: c.linkedin_url, label: 'LinkedIn', color: 'hover:bg-[#0A66C2]' },
  ].filter(s => s.href);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="relative overflow-hidden text-white" data-testid="footer">
      {/* Layered gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2444] to-[#0A1628]"></div>
      <div className="absolute -top-32 left-1/4 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-32 right-1/4 w-96 h-96 bg-rose-500/15 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-[#1565C0]/30 rounded-full blur-3xl"></div>

      {/* Top CTA strip */}
      <div className="relative border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 mb-2 text-amber-300 text-xs font-bold uppercase tracking-widest">
                <Sparkles className="w-4 h-4" />
                {c.cta_kicker}
              </div>
              <h3 className="text-2xl md:text-3xl font-serif font-bold leading-tight">
                {c.cta_headline_prefix}{' '}
                <span className="italic bg-gradient-to-r from-amber-300 to-rose-300 bg-clip-text text-transparent">
                  {c.cta_headline_emphasis}
                </span>
              </h3>
            </div>
            <button
              onClick={() => scrollToSection('donate')}
              className="group inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-7 py-4 rounded-full font-black uppercase tracking-wider text-sm shadow-2xl hover:shadow-amber-500/50 hover:scale-105 transition-all whitespace-nowrap"
              data-testid="footer-donate-btn"
            >
              <Heart className="w-5 h-5 fill-white" />
              {c.cta_button_label}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Main columns */}
      <div className="relative max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          {/* Brand column */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <button
                onClick={() => { window.location.href = '/'; }}
                className="bg-white/95 inline-block rounded-2xl p-3 shadow-2xl hover:scale-105 transition-transform"
                aria-label="EWRF home"
                data-testid="footer-logo-btn"
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/ewrf_47years_logo.jpg`}
                  alt="EWRF 47 Years Logo"
                  className="h-32 md:h-40 w-auto object-contain"
                />
              </button>
              <a
                href="https://www.hasil.gov.my/en/individual/individual-life-cycle/how-to-declare-income/tax-reliefs/list-of-approved-institutions/"
                target="_blank"
                rel="noopener noreferrer"
                title="EWRF is a tax-exempted organisation (LHDN Ref: AG.600-12/1/4-6.5253)"
                aria-label="Tax Exempted — LHDN AG.600-12/1/4-6.5253"
                className="shrink-0 rounded-full hover:scale-110 transition-transform"
                data-testid="footer-tax-exempted-badge"
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/tax_exempted_badge.jpeg`}
                  alt="Tax Exempted — LHDN AG.600-12/1/4-6.5253"
                  className="h-24 md:h-28 w-auto object-contain drop-shadow-lg"
                />
              </a>
            </div>
            <p className="text-sm text-white/80 leading-relaxed mb-6">
              Educational, Welfare and Research Foundation — empowering underserved communities in Malaysia since
              <span className="font-bold text-amber-300"> 1979 </span>
              through education, counselling, welfare, and volunteer development.
            </p>
            <div className="flex gap-2">
              {socials.map(({ Icon, href, label, color }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`w-10 h-10 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center transition-all hover:scale-110 hover:border-transparent ${color}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Programmes */}
          <div className="md:col-span-2">
            <h4 className="text-amber-300 font-bold uppercase tracking-widest text-xs mb-4">{t('footer.our_programmes')}</h4>
            <ul className="space-y-2 text-sm text-white/75">
              {[
                { label: 'CSI Plus', slug: 'civil-society-intervention-plus' },
                { label: 'Language for Juniors', slug: 'language-for-junior' },
                { label: 'Counselling', slug: 'counselling' },
                { label: 'Career Coaching', slug: 'c3g' },
                { label: 'Welfare & Medical Aid', slug: 'medical-aid-welfare' },
                { label: 'Volunteer Induction', slug: 'vip' },
                { label: 'Alumni', slug: 'alumni' },
                { label: 'Research', slug: 'research' },
                { label: 'Skill Training Initiative (STI)', slug: 'sti' },
              ].map(({ label, slug }) => (
                <li key={slug}>
                  <a
                    href={`/projects/${slug}`}
                    className="hover:text-amber-300 transition-colors text-left"
                    data-testid={`footer-programme-${slug}`}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Media + Quick */}
          <div className="md:col-span-3">
            <h4 className="text-amber-300 font-bold uppercase tracking-widest text-xs mb-4">{t('footer.quick_links')}</h4>
            <ul className="space-y-2 text-sm text-white/75 mb-6">
              <li><a href="/#gallery" className="hover:text-amber-300 transition-colors" data-testid="footer-gallery-link">{t('nav.gallery')}</a></li>
              <li><a href="/#videos" className="hover:text-amber-300 transition-colors" data-testid="footer-videos-link">{t('nav.videos')}</a></li>
              <li><a href="/news" className="hover:text-amber-300 transition-colors" data-testid="footer-news-link">{t('nav.news_events')}</a></li>
            </ul>
            <h4 className="text-amber-300 font-bold uppercase tracking-widest text-xs mb-4">{t('footer.follow_us')}</h4>
            <ul className="space-y-2 text-sm text-white/75">
              <li><a href="/#donate" className="hover:text-amber-300 transition-colors" data-testid="footer-donate-link">{t('footer.donate')}</a></li>
              <li>
                <button
                  type="button"
                  onClick={() => setOpenEnquiry('volunteer')}
                  className="hover:text-amber-300 transition-colors text-left"
                  data-testid="footer-volunteer-link"
                >
                  {t('footer.volunteer_with_us')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setOpenEnquiry('counselling')}
                  className="hover:text-amber-300 transition-colors text-left"
                  data-testid="footer-book-counselling-link"
                >
                  {t('footer.book_counselling')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="text-amber-300 font-bold uppercase tracking-widest text-xs mb-4">{t('footer.contact_info')}</h4>
            <div className="space-y-4 text-sm">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-amber-300" />
                </div>
                <p className="text-white/80 leading-relaxed">
                  {(c.address_lines || []).map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < c.address_lines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              </div>
              <a href={c.phone_href} className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500 group-hover:border-transparent transition-all">
                  <Phone className="w-4 h-4 text-amber-300 group-hover:text-white" />
                </div>
                <span className="text-white/80 group-hover:text-amber-300 transition-colors">{c.phone}</span>
              </a>
              <a href={c.whatsapp_href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-lg bg-[#25D366]/20 border border-[#25D366]/40 flex items-center justify-center flex-shrink-0 group-hover:bg-[#25D366] transition-all">
                  <Phone className="w-4 h-4 text-[#25D366] group-hover:text-white" />
                </div>
                <span className="text-white/80 group-hover:text-[#25D366] transition-colors">{c.whatsapp_display}</span>
              </a>
              <a href={`mailto:${c.email}`} className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500 group-hover:border-transparent transition-all">
                  <Mail className="w-4 h-4 text-amber-300 group-hover:text-white" />
                </div>
                <span className="text-white/80 group-hover:text-amber-300 transition-colors break-all">{c.email}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="border-t border-white/10 pt-6 pb-2 md:pr-24 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <p>
            {c.copyright_text}
            <span className="mx-2 text-white/30">·</span>
            <a href="/admin" className="hover:text-amber-300 transition-colors" data-testid="footer-admin-link" title="Admin Login">Admin</a>
          </p>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-amber-300 transition-colors" data-testid="footer-privacy-link">Privacy</a>
            <a href="/terms" className="hover:text-amber-300 transition-colors" data-testid="footer-terms-link">Terms</a>
          </div>
        </div>

        {/* IT Partner credit */}
        <div
          className="mt-4 pt-3 border-t border-white/5 text-center text-[11px] tracking-wide text-white/50"
          data-testid="footer-it-partner"
        >
          IT Partner · Website Designed &amp; Developed by{' '}
          <span className="font-semibold text-amber-300/90">MOGATECH IT SYS AND SERVICES</span>
        </div>
      </div>

      {/* Enquiry dialogs — mounted at footer root so they overlay above everything */}
      <EnquiryDialog
        open={openEnquiry === 'volunteer'}
        onOpenChange={(v) => !v && setOpenEnquiry(null)}
        type="volunteer"
      />
      <EnquiryDialog
        open={openEnquiry === 'counselling'}
        onOpenChange={(v) => !v && setOpenEnquiry(null)}
        type="counselling"
      />
    </footer>
  );
};

export default Footer;
